import Image from "next/image";
import Link from "next/link";
import type { BlogFrontmatter } from "@/lib/types";

function MuteTaxesIcon() {
  return (
    <svg
      width="36"
      height="29"
      viewBox="0 0 35 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient
          id="mt-gradient"
          x1="0"
          y1="7.93"
          x2="36"
          y2="32.49"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#6896FF" />
          <stop offset="1" stopColor="#004EFF" />
        </linearGradient>
      </defs>
      <path
        d="M0 27.9998V7.98142L7.98657 0.00113394H10.0156V8.2909L2.37439 15.9617V17.6373L2.46073 17.5491L20.0733 -0.000488281H22.1455V8.28928L14.5064 15.9617V17.6373C16.175 15.9612 18.0597 14.0799 20.1607 11.9935C22.2617 9.90706 24.3842 7.79053 26.5282 5.64388C28.6723 3.49776 30.5503 1.6164 32.1621 -0.000217912H34.2343V8.28955L26.5931 15.9617V17.6373L34.2343 10.0087V20.3695L26.5931 27.9998H24.2187V18.299L14.5064 27.9998H12.131V18.299L2.37439 27.9998H0Z"
        fill="url(#mt-gradient)"
      />
    </svg>
  );
}

function BlogCard({ blog }: { blog: BlogFrontmatter }) {
  const metaLine = [blog.date, blog.readingTime]
    .filter(Boolean)
    .join(" \u2022 ");
  const author = blog.author || "MuteTaxes Team";

  return (
    <div className="flex flex-col sm:flex-row gap-5 bg-[#0f1218] md:h-[415px] rounded-2xl p-4">
      {/* Image */}
      <div className="relative shrink-0 w-full sm:w-[300px] md:w-[433px] h-[220px] sm:h-auto rounded-xl overflow-hidden bg-[#3D4A5C]">
        {blog.coverImage && (
          <Image
            src={blog.coverImage}
            alt={blog.title}
            fill
            className="object-cover"
          />
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col justify-between gap-5 py-2 px-1 flex-1">
        <div className="flex flex-col gap-4">
          {/* Category Badge */}
          {blog.category && (
            <span className="self-start text-[13px] sm:text-[16px] leading-[24px] font-inter font-medium px-3 sm:px-4 py-1 sm:py-1.5 rounded-full bg-white/10 border border-white/10 text-[#6fa6ff]">
              {blog.category}
            </span>
          )}

          {/* Title */}
          <h2 className="font-inter whitespace-pre-line font-semibold text-[22px] sm:text-[26px] md:text-[32px] leading-[110%] tracking-[-1px] text-white">
            {blog.title}
          </h2>

          {/* Description */}
          {blog.excerpt && (
            <div className="font-inter whitespace-pre-line text-[14px] sm:text-[16px] font-medium text-white/70 leading-[24px]">
              {blog.excerpt}
            </div>
          )}
        </div>

        {/* Author Row */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <MuteTaxesIcon />
            <div className="flex flex-col leading-tight gap-0.5">
              <span className="text-[14px] font-inter font-semibold text-white">
                {author}
              </span>
              {metaLine && (
                <span className="text-[12px] font-inter text-white/60">
                  {metaLine}
                </span>
              )}
            </div>
          </div>

          <Link
            href={`/blogs/${blog.slug}`}
            className="flex items-center gap-2 bg-transparent border border-white/10 rounded-lg px-4 py-2 text-[13px] font-inter font-medium text-[#6fa6ff] hover:bg-white/5 transition whitespace-nowrap shrink-0"
          >
            Read article
            <svg
              width="7"
              height="12"
              viewBox="0 0 7 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 1L6 6L1 11"
                stroke="#6fa6ff"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default BlogCard;
